import { useEffect, useState, type RefObject } from "react";

const SCROLL_CONTAINER_DEFAULT_HEIGHT = 600;
const RESIZE_DEBOUNCE_MS = 200;

/**
 * Tracks the live `clientHeight` of `scrollAreaRef`.
 *
 *   • Initial measurement is synchronous so the first render of
 *     `useMessageWindow` sees the real viewport height.
 *   • Subsequent measurements are debounced (200ms — Signal Desktop
 *     uses 500ms for the equivalent callback). During typing the
 *     compose area resizes frequently, and an unthrottled callback
 *     cascades through `useMessageWindow → setBounds → full re-render`.
 */
export function useViewportHeight(
  scrollAreaRef: RefObject<HTMLDivElement | null>,
): number {
  const [viewportHeight, setViewportHeight] = useState(
    SCROLL_CONTAINER_DEFAULT_HEIGHT,
  );

  useEffect(() => {
    const el = scrollAreaRef.current;
    if (!el) return;
    const initial = el.clientHeight;
    if (initial > 0) setViewportHeight(initial);

    let timerId: ReturnType<typeof setTimeout> | null = null;
    const ro = new ResizeObserver(() => {
      if (timerId !== null) clearTimeout(timerId);
      timerId = setTimeout(() => {
        timerId = null;
        const h = el.clientHeight;
        if (h > 0) setViewportHeight(h);
      }, RESIZE_DEBOUNCE_MS);
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      if (timerId !== null) clearTimeout(timerId);
    };
  }, [scrollAreaRef]);

  return viewportHeight;
}
